import { ArrowUpRight } from "lucide-react";
import { PortfolioDataItem } from "@/types/types";
import CountUp from "../animatedComponents/CountUp";

type ViewMode = "creatives" | "videos" | "websites";

interface PortfolioLandingProps {
  groups: PortfolioDataItem[];
  onSelect: (view: ViewMode) => void;
  onCategorySelect: (category: string) => void;
}

const PortfolioLanding: React.FC<PortfolioLandingProps> = ({
  groups,
  onSelect,
  onCategorySelect,
}) => {
  const categories = Array.from(new Set(groups.map((item) => item.title)));
  const brands = Array.from(new Set(groups.map((item) => item.brand)));
  const creativeCount = groups
    .filter((item) => item.type === "image")
    .reduce((total, item) => total + item.media.length, 0);
  const videoCount = groups
    .filter((item) => item.type === "video")
    .reduce((total, item) => total + item.media.length, 0);
  const websiteCount = groups
    .filter((item) => item.type === "website")
    .reduce((total, item) => total + item.media.length, 0);
  const views: number = 900;

  const cards = [
    {
      id: "01",
      title: "Creatives",
      description: "Static design work — campaigns, posts, launches.",
      view: "creatives" as const,
    },
    {
      id: "02",
      title: "Videos",
      description: "Short-form videos we've shot and edited.",
      view: "videos" as const,
    },
    {
      id: "03",
      title: "Websites",
      description: "Responsive web experiences built for conversion.",
      view: "websites" as const,
    },
  ];

  const getCardCount = (view: ViewMode) => {
    if (view === "creatives") return creativeCount;
    if (view === "videos") return videoCount;
    return websiteCount;
  };

  return (
    <main className="min-h-screen bg-[#0d1821] bg-[radial-gradient(circle_at_80%_65%,rgba(38,78,32,0.45),transparent_34%),#031316] px-6 pt-16 text-[#f5f5f5] md:px-12 lg:px-0 lg:pt-24">
      <div className="mx-auto max-w-[1350px]">
        <h1 className="mt-8 max-w-2xl uppercase font-orbitron text-4xl leading-[1.3] tracking-normal md:text-5xl">
          Creative work,
          <br />
          built to{" "}
          <span className="text-[#c3f620] uppercase font-semibold">move.</span>
        </h1>

        <p className="mt-7 max-w-xl font-poppins text-base leading-7 text-[#f5f5f5]/80 md:text-lg">
          We craft static creatives and short-form videos for brands across{" "}
          {categories.length} industries. Pick a side to begin.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {cards.map((card) => (
            <button
              key={card.id}
              type="button"
              onClick={() => onSelect(card.view)}
              className="group min-h-[148px] rounded-lg border border-[#f5f5f5]/15 bg-[#0d1821]/70 p-6 text-left font-poppins outline-none ring-[#00a878] transition hover:border-[#00a878]/70 hover:bg-[#102129] focus-visible:ring-2 md:min-h-[154px]"
            >
              <div className="flex items-start justify-between gap-4">
                <span className="text-sm tracking-[0.18em] text-[#c3f620] font-poppins uppercase">
                  {getCardCount(card.view)}+
                </span>
                <ArrowUpRight className="h-5 w-5 text-[#f5f5f5] transition group-hover:text-[#c3f620]" />
              </div>
              <h2 className="mt-8 text-2xl font-normal leading-tight tracking-[1px] md:text-3xl font-orbitron uppercase">
                {card.title}
              </h2>
              <p className="mt-2 max-w-[260px] text-sm leading-5 text-[#f5f5f5]/80">
                {card.description}
              </p>
            </button>
          ))}
        </div>

        <div className="mt-12 flex gap-8 font-poppins md:gap-20 text-center md:text-left">
          <div>
            <CountUp
              start={0}
              end={categories.length}
              duration={2}
              className="text-3xl font-bold leading-none"
            />
            <span className="text-2xl font-bold leading-none"> +</span>
            <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#f5f5f5]">
              Categories
            </p>
          </div>
          <div>
            <CountUp
              start={0}
              end={brands.length}
              duration={2}
              className="text-3xl font-bold leading-none"
            />
            <span className="text-2xl font-bold leading-none"> +</span>
            <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#f5f5f5]">
              Brands
            </p>
          </div>
          <div>
            <CountUp
              start={0}
              end={views}
              duration={2}
              className="text-3xl font-bold leading-none"
            />
            <span className="text-2xl font-bold leading-none"> K+</span>
            <p className="mt-2 text-xs uppercase tracking-[0.22em] text-[#f5f5f5]">
              Views Reached
            </p>
          </div>
        </div>

        <div className="mt-12 border-t border-[#f5f5f5]/18 py-12">
          <h2 className="font-poppins text-2xl font-bold">
            Industries we work with
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => onCategorySelect(category)}
                className="rounded-full border border-[#00a878]/40 bg-[#0d1821] px-4 py-2 font-poppins text-sm font-medium text-[#f5f5f5] outline-none ring-[#00a878] transition hover:border-[#00a878] hover:bg-[#00a878] focus-visible:ring-2"
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

export default PortfolioLanding;
